import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type {
  CreateEventsRequest,
  EventSignUpRequest,
  RegisterEventParticipantRequest,
  UpdateEventParticipantRequest,
  UpdateEventRequest,
} from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useAddEvents = () => {
  const projectId = useProjectId();
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: CreateEventsRequest) => api.postEventsV2(payload).then(({ data }) => data),
    onSuccess: (data) => {
      showFlashToast({
        variant: "success",
        message: t("page.event.create-or-edit.create-mode.action.create.notification.success"),
      });

      return data;
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.event.create-or-edit.create-mode.action.create.notification.failure"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId), refetchType: "none" });
    },
  });
};

const useUpdateEvent = () => {
  const projectId = useProjectId();
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (payload: { eventId: string; data: UpdateEventRequest }) =>
      api.putEventsV1(payload.eventId, payload.data).then(({ data }) => data),
    onSuccess: (data) => {
      showFlashToast({
        variant: "success",
        message: t("page.event.create-or-edit.edit-mode.action.edit.notification.success"),
      });

      return data;
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("page.event.create-or-edit.edit-mode.action.edit.notification.failure"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId), refetchType: "none" });
    },
  });
};

const useDeleteEvent = () => {
  const projectId = useProjectId();
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (eventId: string) => api.deleteEventsByIdV1(eventId).then(({ data }) => data),
    onSuccess: () => {
      showFlashToast({
        variant: "success",
        message: t("model.event.action.delete.notification.success"),
      });
    },
    onError: () => {
      showFlashToast({
        variant: "alert",
        message: t("model.event.action.delete.notification.error"),
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId),
      });
    },
  });
};

const useEventSignUp = () => {
  const projectId = useProjectId();
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: { eventId: string; guestCount: number }) =>
      api.postEventsSignupV1(payload.eventId, { guestCount: payload.guestCount } satisfies EventSignUpRequest),
    onSettled: (_, __, payload) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EVENT_DETAILS(projectId, payload.eventId),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES(projectId) });
    },
  });
};

const useEventSignOff = () => {
  const projectId = useProjectId();
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (eventId: string) => api.postEventsSignoffV1(eventId),
    onSettled: (_, __, eventId) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EVENT_DETAILS(projectId, eventId),
      });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.MESSAGES(projectId) });
    },
  });
};

const useRegisterEventParticipant = () => {
  const projectId = useProjectId();
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ eventId, data }: { eventId: string; data: RegisterEventParticipantRequest }) =>
      api.postEventsParticipantsV1(eventId, data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.event.participants.add.notification.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("common.generic-error") });
    },
    onSettled: (_, __, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EVENT_DETAILS(projectId, eventId) });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EVENT_PARTICIPANT_CANDIDATES_ALL(projectId, eventId),
      });
    },
  });
};

const useUpdateEventParticipant = () => {
  const projectId = useProjectId();
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ eventId, userId, data }: { eventId: string; userId: string; data: UpdateEventParticipantRequest }) =>
      api.putEventsParticipantsV1(eventId, userId, data),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.event.participants.edit.notification.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("common.generic-error") });
    },
    onSettled: (_, __, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EVENT_DETAILS(projectId, eventId) });
    },
  });
};

const useRemoveEventParticipant = () => {
  const projectId = useProjectId();
  const api = useApi();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ eventId, userId }: { eventId: string; userId: string }) =>
      api.deleteEventsParticipantsByIdV1(eventId, userId),
    onSuccess: () => {
      showFlashToast({ variant: "success", message: t("page.event.participants.remove.notification.success") });
    },
    onError: () => {
      showFlashToast({ variant: "alert", message: t("common.generic-error") });
    },
    onSettled: (_, __, { eventId }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EVENT_LIST_ALL(projectId) });
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.EVENT_DETAILS(projectId, eventId) });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.EVENT_PARTICIPANT_CANDIDATES_ALL(projectId, eventId),
      });
    },
  });
};

export const eventMutations = {
  useAddEvents,
  useUpdateEvent,
  useDeleteEvent,
  useEventSignUp,
  useEventSignOff,
  useRegisterEventParticipant,
  useUpdateEventParticipant,
  useRemoveEventParticipant,
};
